import { showMessageModal } from '~/clientUtils/showMessageModal';
import Checkbox from '~/components/commonUi/Checkbox/Checkbox';
import DateTimePicker from '~/components/commonUi/DateTimePicker/DateTimePicker';
import FileInput from '~/components/commonUi/FileInput/FileInput';
import Input from '~/components/commonUi/Input/Input';
import SelectText from '~/components/commonUi/SelectText/SelectText';
import TextArea from '~/components/commonUi/TextArea/TextArea';
import { CANDIDATE_OPTIONS } from '~/consts/candidates';
import { CandidateKey, EventProps, RenderFieldByTypeParams } from '~/definitions/candidates';

const renderFieldByType = (
    params: RenderFieldByTypeParams,
    fieldsKeyWithIssues: CandidateKey[],
    isCreateMode: boolean
) => {
    const { fieldConfig, value, onChange } = params;
    const { type, id, key, name, accept, disabled } = fieldConfig;

    // Handle gender on Details page, options data from PIM
    if (['gender'].includes(key)) {
        return null;
    }

    const options = [...(CANDIDATE_OPTIONS[key as keyof typeof CANDIDATE_OPTIONS] || [])];

    const getOptions = () => {
        return (
            options.map((option) => ({
                value: option.value,
                label: option.label,
            })) || []
        );
    };

    const onDateOfBirthChange = (value: string) => {
        try {
            onChange({
                target: {
                    name: 'dateOfBirth',
                    value: value,
                },
            } as unknown as EventProps);
        } catch {
            showMessageModal('error', 'Incorrect date');
        }
    };

    const isError = Boolean(fieldsKeyWithIssues.find((k) => k === key)?.length);

    let isCheckboxDisabled = false;
    if ((disabled || !isCreateMode) && id === 'candidate_consent') {
        isCheckboxDisabled = true;
    }

    switch (type) {
        case 'datepicker':
            return (
                <div className="flex gap-2 flex-col md:flex-row">
                    <DateTimePicker
                        testId={id}
                        value={value as string}
                        onChange={onDateOfBirthChange}
                        enableTime={false}
                        containerClassName={'w-full'}
                        disabled={disabled}
                        allowInput={true}
                    />
                </div>
            );
        case 'boolean':
            if (id === 'preCumulated') {
                return (
                    <SelectText
                        id={id}
                        testId={id}
                        name={key}
                        value={value as string}
                        onChange={onChange}
                        options={getOptions()}
                        extendedClassName="w-full"
                        disabled={disabled}
                    />
                );
            }
            return <Checkbox id={id} name={key} checked={!!value} onChange={onChange} disabled={disabled} />;
        case 'text':
            return (
                <Input
                    id={id}
                    name={key}
                    testId={id}
                    value={value as string}
                    onChange={onChange}
                    extendedClassName={'w-full'}
                    error={isError}
                    disabled={disabled}
                />
            );
        case 'checkbox':
            return (
                <Checkbox
                    id={id}
                    name={key}
                    checked={!!value}
                    label={name}
                    onChange={onChange}
                    labelClassNames={'font-normal'}
                    error={isError}
                    disabled={isCheckboxDisabled}
                />
            );
        case 'select':
            return (
                <SelectText
                    id={id}
                    testId={id}
                    name={key}
                    value={value as string}
                    onChange={onChange}
                    options={getOptions()}
                    extendedClassName="w-full"
                    disabled={disabled}
                />
            );
        case 'textarea': {
            let className = '';
            let maxLength = undefined;

            if (id === 'description') {
                className = 'custom-min-height';
                maxLength = 240;
            } else if (id === 'case') {
                maxLength = 40;
            }

            return (
                <TextArea
                    id={id}
                    testId={id}
                    name={key}
                    value={value as string}
                    onChange={onChange}
                    extendedClassName={`w-full ${className}`}
                    maxLength={maxLength}
                    disabled={disabled}
                />
            );
        }
        case 'file':
            return (
                <FileInput
                    id={id}
                    name={key}
                    testId={id}
                    onChange={onChange}
                    extendedClassName={'w-full'}
                    size={'small'}
                    fileName={value as string}
                    accept={accept}
                    disabled={disabled}
                />
            );
        default:
            return null;
    }
};

export default renderFieldByType;
